// AdoptionCurve — step-function clinician adoption chart (M0–M12). Tokens only.
import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

type Props = { bare?: boolean };

const mono = "font-mono uppercase tracking-[0.1em]";
const ease = [0.22, 1, 0.36, 1] as const;

const VB_W = 600;
const VB_H = 280;
const PAD_L = 40;
const PAD_R = 24;
const PAD_T = 24;
const PAD_B = 36;
const PLOT_W = VB_W - PAD_L - PAD_R;
const PLOT_H = VB_H - PAD_T - PAD_B;

const xAt = (m: number) => PAD_L + (m / 12) * PLOT_W;
const yAt = (pct: number) => PAD_T + (1 - pct / 100) * PLOT_H;

const steps: [number, number][] = [
  [0, 5], [1, 12], [2, 25], [3, 35], [4, 52],
  [5, 60], [7, 72], [9, 85], [12, 92],
];

// Smooth curve through points using Catmull-Rom → cubic Bezier conversion.
const buildSmoothPath = () => {
  const pts = steps.map(([m, p]) => [xAt(m), yAt(p)] as const);
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2[0]} ${p2[1]}`;
  }
  return d;
};

const buildSmoothAreaPath = () => {
  const pts = steps.map(([m, p]) => [xAt(m), yAt(p)] as const);
  let d = `M ${pts[0][0]} ${yAt(0)} L ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2[0]} ${p2[1]}`;
  }
  d += ` L ${xAt(12)} ${yAt(0)} Z`;
  return d;
};

const milestones: { m: number; pct: number; label: string; align: "left" | "right"; vAlign: "above" | "below"; labelOffset?: number; textShiftY?: number }[] = [
  { m: 1, pct: 12, label: "FIRST USER",      align: "right", vAlign: "above" },
  { m: 4, pct: 52, label: "50% ADOPTION",    align: "right", vAlign: "above" },
  { m: 9, pct: 85, label: "FULL DEPLOYMENT", align: "left",  vAlign: "above", labelOffset: 14 },
];

const LINE_DUR = 1.6;
// Approx fraction along the path where each milestone sits (M / 12)
const milestoneDelays = [0.2, 0.55, 1.05];

const AdoptionCurve = ({ bare = false }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const reduce = useReducedMotion();
  const show = inView || reduce;

  return (
    <div
      ref={ref}
      className={`relative ${
        bare ? "p-0" : "border border-border bg-[hsl(var(--seabed))] p-6 md:p-12"
      }`}
      style={{ borderRadius: 0 }}
    >

      {!bare && (
        <>
          <span className="absolute top-2 left-2 font-mono text-[10px] leading-none text-foreground/20">+</span>
          <span className="absolute top-2 right-2 font-mono text-[10px] leading-none text-foreground/20">+</span>
          <span className="absolute bottom-2 left-2 font-mono text-[10px] leading-none text-foreground/20">+</span>
          <span className="absolute bottom-2 right-2 font-mono text-[10px] leading-none text-foreground/20">+</span>
          <span className={`absolute top-4 left-4 ${mono} text-[10px] text-muted-foreground`}>FIG. 4.1</span>
        </>
      )}

      <div className="relative">

        <svg
          className="mt-6 w-full"
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          preserveAspectRatio="xMidYMid meet"
          aria-hidden
        >
          <defs>
            <pattern
              id="adopt-hatch"
              patternUnits="userSpaceOnUse"
              width="8"
              height="8"
              patternTransform="rotate(45)"
            >
              <line x1="0" y1="0" x2="0" y2="8" stroke="hsl(var(--primary) / 0.15)" strokeWidth="1" />
            </pattern>
          </defs>

          <line x1={PAD_L} x2={VB_W - PAD_R} y1={yAt(50)}  y2={yAt(50)}  stroke="hsl(var(--border))" strokeWidth="1" strokeDasharray="3 4" />
          <line x1={PAD_L} x2={VB_W - PAD_R} y1={yAt(100)} y2={yAt(100)} stroke="hsl(var(--border))" strokeWidth="1" strokeDasharray="3 4" />

          <line x1={PAD_L} x2={PAD_L} y1={PAD_T} y2={VB_H - PAD_B} stroke="hsl(var(--border))" strokeWidth="1" />
          <line x1={PAD_L} x2={VB_W - PAD_R} y1={VB_H - PAD_B} y2={VB_H - PAD_B} stroke="hsl(var(--border))" strokeWidth="1" />

          {[0, 2, 4, 6, 8, 10, 12].map((m) => (
            <g key={m}>
              <line x1={xAt(m)} x2={xAt(m)} y1={VB_H - PAD_B} y2={VB_H - PAD_B + 4} stroke="hsl(var(--border))" strokeWidth="1" />
              <text
                x={xAt(m)} y={VB_H - PAD_B + 16}
                textAnchor="middle"
                fontFamily="ui-monospace, SFMono-Regular, monospace"
                fontSize="9"
                fill="hsl(var(--muted-foreground))"
                style={{ letterSpacing: "0.1em" }}
              >
                M{m}
              </text>
            </g>
          ))}

          {[0, 50, 100].map((p) => (
            <text
              key={p}
              x={PAD_L - 6} y={yAt(p) + 3}
              textAnchor="end"
              fontFamily="ui-monospace, SFMono-Regular, monospace"
              fontSize="9"
              fill="hsl(var(--muted-foreground))"
              style={{ letterSpacing: "0.1em" }}
            >
              {p}%
            </text>
          ))}

          <motion.path
            d={buildSmoothAreaPath()}
            fill="url(#adopt-hatch)"
            stroke="none"
            initial={{ opacity: 0 }}
            animate={show ? { opacity: 1 } : {}}
            transition={{ duration: reduce ? 0 : 0.8, ease, delay: reduce ? 0 : LINE_DUR * 0.6 }}
          />

          <motion.path
            d={buildSmoothPath()}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={show ? { pathLength: 1 } : {}}
            transition={{ duration: reduce ? 0 : LINE_DUR, ease }}
          />

          {milestones.map(({ m, pct, label, align, vAlign, labelOffset = 0, textShiftY = 0 }, idx) => {
            const cx = xAt(m);
            const cy = yAt(pct);
            const isRight = align === "right";
            const isAbove = vAlign === "above";
            const dx = isRight ? 22 : -22;
            const dy = isAbove ? -32 : 26;
            const tx = cx + dx;
            const ty = cy + dy;
            const textX = tx + (isRight ? 6 + labelOffset : -6 - labelOffset);
            const textY = (isAbove ? ty - 8 : ty + 14) + textShiftY;
            const labelWidth = label.length * 8;
            const base = reduce ? 0 : milestoneDelays[idx] * LINE_DUR;
            return (
              <g key={label}>
                <motion.circle
                  cx={cx} cy={cy} r="6" fill="hsl(var(--primary) / 0.2)"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={show ? { scale: 1, opacity: 1 } : {}}
                  transition={{ duration: reduce ? 0 : 0.4, ease, delay: base }}
                  style={{ transformOrigin: `${cx}px ${cy}px` }}
                />
                {/* Looping ping on every milestone, staggered */}
                {show && !reduce && (
                  <motion.circle
                    cx={cx} cy={cy} r="6" fill="hsl(var(--primary))"
                    initial={{ scale: 1, opacity: 0 }}
                    animate={{ scale: [1, 2], opacity: [0.5, 0] }}
                    transition={{
                      duration: 2.2,
                      ease: "easeOut",
                      delay: 2.6 + idx * 0.4,
                      repeat: Infinity,
                      repeatDelay: 0.6,
                    }}
                    style={{ transformOrigin: `${cx}px ${cy}px` }}
                  />
                )}
                <motion.circle
                  cx={cx} cy={cy} r="4" fill="hsl(var(--primary))"
                  initial={{ scale: 0 }}
                  animate={show ? { scale: 1 } : {}}
                  transition={{ duration: reduce ? 0 : 0.4, ease, delay: base + 0.05 }}
                  style={{ transformOrigin: `${cx}px ${cy}px` }}
                />
                <motion.line
                  x1={cx} y1={cy} x2={tx} y2={ty}
                  stroke="hsl(var(--primary))" strokeWidth="1"
                  initial={{ pathLength: 0 }}
                  animate={show ? { pathLength: 1 } : {}}
                  transition={{ duration: reduce ? 0 : 0.35, ease, delay: base + 0.15 }}
                />
                <motion.g
                  initial={{ opacity: 0 }}
                  animate={show ? { opacity: 1 } : {}}
                  transition={{ duration: reduce ? 0 : 0.35, ease, delay: base + 0.4 }}
                >
                  <rect
                    x={isRight ? textX - 4 : textX - labelWidth - 4}
                    y={textY - 10}
                    width={labelWidth + 8}
                    height="14"
                    fill="hsl(var(--seabed))"
                  />
                  <text
                    x={textX}
                    y={textY}
                    textAnchor={isRight ? "start" : "end"}
                    fontFamily="ui-monospace, SFMono-Regular, monospace"
                    fontSize="10"
                    fill="hsl(var(--primary))"
                    style={{ letterSpacing: "0.1em" }}
                  >
                    {label}
                  </text>
                </motion.g>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

export default AdoptionCurve;
